import { store as starkStore } from '@ice/stark-data';

/**
 * HERE Maps JSAPI v3.1 接入。
 *
 * ============ 为什么这套代码长这样 ============
 *
 * HERE 给我们的凭证是 OAuth 2.0 类型(accessKeyId + accessKeySecret),不是 API Key。
 * 这两种凭证在 HERE 体系里走完全不同的认证通道:
 *   - API Key:一个长字符串,直接 `new H.service.Platform({ apikey })` 完事
 *   - OAuth 凭证:本应在服务端用,通过 /oauth2/token 端点签发短期 JWT
 *
 * HERE 没给我们 API Key 的途径(后端确认过)。所以这里**前端在做本应服务端做的事**:
 *   1. 用 OAuth 1.0 HMAC-SHA256 签名调 /oauth2/token 拿 JWT(标准签名套件,无解)
 *   2. 把 JWT 通过 Platform 构造函数的 `headers` 字段塞进 SDK
 *
 * 第 2 步用到的 `headers` 字段在 HERE 公开文档里**没有写**,但反编译 mapsjs-service.js
 * 能看到 Platform 构造函数确实接受它,会把内容 merge 进所有 *.hereapi.com 的请求头。
 * 这是当前唯一能让 SDK 用 JWT 的官方机制(其他做法比如 fetch/XHR 全局 patch 都更糟)。
 *
 * ============ 已知约束 ============
 *
 * - JWT 有 TTL(从 /oauth2/token 响应 expires_in 字段拿,实测约 86400s = 24h)。
 *   当前**不实现自动刷新**,用户开页面超过 TTL 后,HERE 服务调用会 401。
 *   重新刷新页面即恢复。24h 一般跨过用户关机时间,实际场景几乎不会撞到。
 * - `apikey` 字段必填,空值会被构造函数拒绝,所以塞个无意义占位串。
 * - 子应用通过 starkStore 的 `here:platform` 用 SDK 调 reverseGeocode 等服务,
 *   认证封装在 platform.headers 内部,token 不需要广播。
 */
const HERE_CREDENTIALS = {
  accessKeyId: 'lUZ3LExeB3L-pf0cbROOHw',
  accessKeySecret:
    'JGWbD7W8yNxYg2lm9WZrH3he3tPAgDqTc3b9HHeJv852owz4hn4GWTJZpy9YkKicJ5lM9XRZgTlDWCJA_4ee_Q',
  tokenUrl: 'https://account.api.here.com/oauth2/token',
};

type HerePlatformHandle = {
  H: any;
  platform: any;
  defaultLayers: any;
};

let cachedLoadPromise: Promise<HerePlatformHandle> | null = null;
let cachedHandle: HerePlatformHandle | null = null;

function randomNonce(): string {
  const arr = new Uint8Array(16);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

function rfc3986Encode(str: string): string {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase(),
  );
}

async function hmacSha256Base64(key: string, data: string): Promise<string> {
  const enc = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(data));
  let binary = '';
  const bytes = new Uint8Array(sig);
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

/**
 * HERE Platform 的 /oauth2/token 端点要求 OAuth 1.0 签名(不是 OAuth 2.0,HERE 自己设计的怪东西)。
 * 签名 base string:METHOD & rfc3986(URL) & rfc3986(sortedParams)
 * signing key:rfc3986(secret) + '&'
 */
async function fetchHereToken(): Promise<
  [{ accessToken: string; expiresInSec: number } | null, Error | null]
> {
  const url = HERE_CREDENTIALS.tokenUrl;
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const nonce = randomNonce();

  const oauthParams: Record<string, string> = {
    oauth_consumer_key: HERE_CREDENTIALS.accessKeyId,
    oauth_nonce: nonce,
    oauth_signature_method: 'HMAC-SHA256',
    oauth_timestamp: timestamp,
    oauth_version: '1.0',
  };
  const bodyParams: Record<string, string> = { grant_type: 'client_credentials' };
  const allParams = { ...oauthParams, ...bodyParams };

  const paramString = Object.keys(allParams)
    .sort()
    .map((k) => `${rfc3986Encode(k)}=${rfc3986Encode(allParams[k])}`)
    .join('&');
  const baseString = `POST&${rfc3986Encode(url)}&${rfc3986Encode(paramString)}`;
  const signingKey = `${rfc3986Encode(HERE_CREDENTIALS.accessKeySecret)}&`;

  let signature: string;
  try {
    signature = await hmacSha256Base64(signingKey, baseString);
  } catch (e) {
    return [null, e as Error];
  }

  const authHeader =
    'OAuth ' +
    Object.entries({ ...oauthParams, oauth_signature: signature })
      .map(([k, v]) => `${rfc3986Encode(k)}="${rfc3986Encode(v)}"`)
      .join(',');

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: 'grant_type=client_credentials',
    });
  } catch (e) {
    return [null, e as Error];
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    return [null, new Error(`Here token endpoint ${resp.status}: ${text}`)];
  }

  const json = await resp.json().catch(() => null);
  if (!json?.access_token) {
    return [null, new Error(`Here token response missing access_token: ${JSON.stringify(json)}`)];
  }

  return [
    {
      accessToken: json.access_token,
      expiresInSec: typeof json.expires_in === 'number' ? json.expires_in : 3600,
    },
    null,
  ];
}

function injectHereUiCss() {
  if (typeof document === 'undefined') return;
  const id = 'here-maps-ui-css';
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = 'https://js.api.here.com/v3/3.1/mapsjs-ui.css';
  document.head.appendChild(link);
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = false;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load Here script: ${src}`));
    document.head.appendChild(s);
  });
}

// 通过 CDN 按序加载 HERE Maps JSAPI v3.1;依赖顺序: core → service → mapevents → ui → clustering
async function loadHereScriptsFromCdn(): Promise<any> {
  const base = 'https://js.api.here.com/v3/3.1';
  const scripts: Array<[string, string]> = [
    [`${base}/mapsjs-core.js`, 'here-core'],
    [`${base}/mapsjs-service.js`, 'here-service'],
    [`${base}/mapsjs-mapevents.js`, 'here-mapevents'],
    [`${base}/mapsjs-ui.js`, 'here-ui'],
    [`${base}/mapsjs-clustering.js`, 'here-clustering'],
  ];
  for (const [src, id] of scripts) {
    await loadScript(src, id);
  }
  const H = (window as any).H;
  if (!H?.service?.Platform) {
    throw new Error('Here SDK loaded but window.H.service.Platform missing');
  }
  return H;
}

export async function ensureHereSdkLoaded(): Promise<HerePlatformHandle> {
  if (cachedHandle) return cachedHandle;
  if (cachedLoadPromise) return cachedLoadPromise;

  cachedLoadPromise = (async () => {
    const [token, tokenErr] = await fetchHereToken();
    if (tokenErr || !token) {
      starkStore.set('here:loaded', false);
      starkStore.set('here:error', tokenErr);
      throw tokenErr ?? new Error('Here token fetch failed');
    }

    // 用真实 expires_in 观察 TTL,决定将来要不要做刷新
    console.log('[HERE] token TTL (s):', token.expiresInSec);

    injectHereUiCss();
    const H = await loadHereScriptsFromCdn();

    const platform = new H.service.Platform({
      // apikey 字段是 SDK 强校验的非空必填,塞占位符即可。真正认证靠下面的 headers
      apikey: 'placeholder-using-oauth-bearer',
      // 反编译 mapsjs-service.js 验证:headers 会被 merge 进所有 *.hereapi.com 请求
      headers: { Authorization: `Bearer ${token.accessToken}` },
    });
    const defaultLayers = platform.createDefaultLayers();

    cachedHandle = { H, platform, defaultLayers };

    starkStore.set('here:instance', H);
    starkStore.set('here:platform', platform);
    starkStore.set('here:defaultLayers', defaultLayers);
    starkStore.set('here:loaded', true);

    return cachedHandle;
  })().catch((err) => {
    cachedLoadPromise = null;
    console.error('[HERE] ensureHereSdkLoaded failed', err);
    throw err;
  });

  return cachedLoadPromise;
}
